// Shared helpers + constants for the Companies (company-scoped claim browser) page.
import { normalizeStatus } from '../../utils/claimHelpers';

export const FREQ_TO_DAYS = { daily: 1, '3_days': 3, '1_week': 7, '2_weeks': 14, '30_days': 30 };

/** Overdue badge resolver for a single claim, given the tenant's frequency in days. */
export function getOverdueInfo(claim, freqDays) {
  if (!freqDays || !claim.last_activity_at) return null;
  const status = claim.status || 'New';
  if (status === 'Closed') return null;
  const lastActivity = new Date(claim.last_activity_at);
  const elapsed = (Date.now() - lastActivity.getTime()) / (1000 * 60 * 60 * 24);
  const remaining = freqDays - elapsed;
  if (remaining <= 0) {
    return { type: 'overdue', label: `Overdue ${Math.ceil(Math.abs(remaining))}d` };
  }
  if (remaining <= 1) return { type: 'due', label: 'Due today' };
  return null;
}

/** Phase D — freshness badge for the last customer email update sent on a claim. */
export function getCustomerUpdateFreshness(lastAt) {
  if (!lastAt) return null;
  const when = new Date(lastAt);
  if (isNaN(when.getTime())) return null;
  const mins = Math.floor((Date.now() - when.getTime()) / 60000);
  const absolute = when.toLocaleString();
  if (mins < 1) return { label: 'just now', absolute };
  if (mins < 60) return { label: `${mins}m`, absolute };
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return { label: `${hrs}h`, absolute };
  const days = Math.floor(hrs / 24);
  if (days < 30) return { label: `${days}d`, absolute };
  return { label: `${Math.floor(days / 30)}mo`, absolute };
}

export const ALL_COLUMNS = [
  { id: 'report_no', label: 'Report No', default: true },
  { id: 'date_of_loss', label: 'Date Of Loss', default: true },
  { id: 'truck_no', label: 'Truck', default: true },
  { id: 'trailer_no', label: 'Trailer', default: true },
  { id: 'drivers_name', label: 'Driver', default: true },
  { id: 'drivers_ph_no', label: 'Driver Phone', default: false },
  { id: 'fault', label: 'Fault', default: true },
  { id: 'age', label: 'Age', default: true },
  { id: 'updated_at', label: 'Updated', default: true },
  { id: 'update_age', label: 'Update Age', default: true },
  { id: 'created_at', label: 'Created', default: true },
  { id: 'location', label: 'Location', default: false },
  { id: 'description', label: 'Description', default: false },
  { id: 'status', label: 'Status', default: false },
  { id: 'police_involved', label: 'Police Involved', default: false },
  { id: 'other_vehicle_involved', label: 'Other Vehicle', default: false },
  { id: 'injury', label: 'Injury', default: false },
  { id: 'tow_away', label: 'Tow Away', default: false },
  { id: 'fatality', label: 'Fatality', default: false },
];

export const LOSS_TYPES = [
  { key: 'al', label: 'Auto Liability' },
  { key: 'pd', label: 'Physical Damage' },
  { key: 'cd', label: 'Cargo Damage' },
  { key: 'ocac', label: 'Occupational Accident' },
  { key: 'wc', label: 'Workers Compensation' },
];

export const STATUS_TYPES = [
  { key: 'new', label: 'New' },
  { key: 'in_process', label: 'In Process' },
  { key: 'closed', label: 'Closed' },
];

/** Map a status-tab key (e.g. 'new') to its canonical display label (e.g. 'New'). */
export const STATUS_KEY_TO_LABEL = STATUS_TYPES.reduce((acc, s) => {
  acc[s.key] = s.label;
  return acc;
}, {});

export const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: '2-digit', day: '2-digit', year: 'numeric', timeZone: 'UTC',
  });
};

export const calculateAge = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const diff = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'today';
  if (diff === 1) return '1 day';
  if (diff < 30) return `${diff} days`;
  const months = Math.floor(diff / 30);
  return months === 1 ? '1 month' : `${months} months`;
};

/** Plain-text cell value used for column filtering. */
export function getCellText(claim, colId, activeLossType) {
  switch (colId) {
    case 'report_no':       return claim.report_no || '';
    case 'date_of_loss':    return formatDate(claim.date_of_loss);
    case 'truck_no':        return claim.truck_no || '';
    case 'trailer_no':      return claim.trailer_no || '';
    case 'drivers_name':    return claim.drivers_name || '';
    case 'drivers_ph_no':   return claim.drivers_ph_no || '';
    case 'fault':           return claim.fault || '';
    case 'age':             return calculateAge(claim.date_of_loss);
    case 'updated_at':      return formatDate(claim.updated_at);
    case 'update_age':      return calculateAge(claim.updated_at);
    case 'created_at':      return formatDate(claim.created_at);
    case 'location':        return claim.location || '';
    case 'description':     return claim.description || '';
    case 'status': {
      const ts = claim.type_data?.[activeLossType]?.status || claim.status || '';
      return normalizeStatus(ts);
    }
    case 'police_involved': return claim.police_involved === true ? 'Yes' : claim.police_involved === false ? 'No' : '';
    case 'other_vehicle_involved': return claim.other_vehicle_involved || '';
    case 'injury':          return claim.injury ? 'Yes' : 'No';
    case 'tow_away':        return claim.tow_away ? 'Yes' : 'No';
    case 'fatality':        return claim.fatality ? 'Yes' : 'No';
    default:                return '';
  }
}

/** Roll the per-loss-type statuses up into a single global status for this claim. */
export function getEntryStatus(claim) {
  const td = claim.type_data || {};
  const activeStatuses = ['al', 'pd', 'cd', 'ocac', 'wc']
    .filter((k) => claim[k])
    .map((k) => normalizeStatus(td[k]?.status || claim.status || 'New'));
  if (activeStatuses.length === 0) return normalizeStatus(claim.status || 'New');
  if (activeStatuses.every((s) => s === 'Closed')) return 'Closed';
  if (activeStatuses.some((s) => s === 'In Process')) return 'In Process';
  return 'New';
}
